import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import type { Cluster } from '../types'
import { useClipReady } from '../useClipReady'
import { ClusterClipPanel } from './ClusterClipPanel'
import { incidentTitle } from '../incidentTitle'
import type { StreamLayout } from './StreamPanel'

interface Props {
  /** The incident open on this camera, or none for plain live view. */
  cluster: Cluster | null
  onBack: () => void
  /**
   * The camera's live stream, placed where this view says. App builds it,
   * since it holds the stream; it is one element in one place whatever the
   * layout, so it is never started afresh (#287).
   */
  stream: (layout: StreamLayout, overlay?: ReactNode, cornerTop?: number) => ReactNode
  /** Opens the speak panel; absent where the camera cannot take a message. */
  onSpeak?: () => void
  /**
   * Live in the clip's corner. Off on a phone, where the corner would be a
   * thumbnail over a clip barely larger, so live is a button away instead.
   */
  corner?: boolean
  /** The back button's label: the view it returns to. */
  backLabel?: string
  /**
   * The site keeps no recordings VMS can reach, so an incident's clip can
   * never load: the camera live takes its place, said to be live (#294).
   */
  noRecordings?: boolean
}

// CameraView is a camera: its live view, and an incident open on it — the clip
// large with live in its corner, since a dispatcher reviewing what happened
// still wants what is happening now, and live large while it still records or
// where the site keeps no recordings.
export function CameraView({ cluster, onBack, stream, onSpeak, corner: withCorner = true, backLabel = 'Sites', noRecordings = false }: Props) {
  // An incident is keyed by id so its view state starts fresh with each.
  const [view, setView] = useState<{ id: number | null; liveLarge: boolean; liveHidden: boolean }>({ id: null, liveLarge: false, liveHidden: false })
  const current = view.id === (cluster?.id ?? null) ? view : { id: cluster?.id ?? null, liveLarge: false, liveHidden: false }
  // Each control is replaced by its opposite when pressed; focus follows to
  // the one that undoes it rather than falling to the page (#287).
  const root = useRef<HTMLDivElement>(null)
  const focusNext = useRef<string | null>(null)
  const update = (patch: Partial<typeof view>, then: string) => {
    focusNext.current = then
    setView({ ...current, ...patch })
  }
  useEffect(() => {
    if (!focusNext.current) return
    root.current?.querySelector<HTMLElement>(`[data-focus="${focusNext.current}"]`)?.focus()
    focusNext.current = null
  })
  // An incident opening takes focus to its name: what opened it has gone.
  const heading = useRef<HTMLHeadingElement>(null)
  const incidentId = cluster?.id
  useEffect(() => {
    if (incidentId !== undefined) heading.current?.focus()
  }, [incidentId])
  const ready = useClipReady(cluster)
  // The clip itself saying the site keeps no recordings, for when the site
  // list has not said so yet.
  const [refused, setRefused] = useState<number | null>(null)
  const refuse = useCallback(() => setRefused(incidentId ?? null), [incidentId])
  const noClip = !!cluster && (noRecordings || refused === cluster.id)
  // The same rule the clip panel shows its recording message on: a tamper
  // clip arrives on its own evidence window, not when the incident ends.
  const recording = !!cluster && !ready && !cluster.all_ended && cluster.label !== 'tamper'
  const tamper = cluster?.label === 'tamper'
  const large = noClip || recording || current.liveLarge

  // The corner sits at the clip's bottom right, above its controls: at the top
  // it covered the date and time a camera burns into its picture (#287).
  const [clipBox, setClipBox] = useState<{ width: number; height: number } | null>(null)
  const cornerWidth = clipBox ? Math.min(clipBox.width * 0.3, 320) : 0
  const cornerTop = clipBox ? Math.max(12, clipBox.height - (cornerWidth * 9) / 16 - 56) : undefined

  const layout: StreamLayout = !cluster ? 'page' : large ? 'large' : !withCorner || current.liveHidden ? 'hidden' : 'corner'
  const overlay = layout === 'corner' ? (
    <>
      <span className="absolute top-2 left-2 h-6 px-2 inline-flex items-center gap-1.5 rounded-md bg-black/65 text-xs font-semibold text-live pointer-events-none">
        <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-current" />
        Live now
      </span>
      <span className="absolute top-1.5 right-1.5 flex gap-1">
        <button type="button" aria-label="Make live large" title="Make live large" data-focus="make-large" onClick={() => update({ liveLarge: true }, 'back-to-clip')} className="w-7 h-7 flex items-center justify-center rounded-md bg-black/65 text-white hover:bg-black/80">
          <svg aria-hidden="true" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </button>
        <button type="button" aria-label="Hide live" title="Hide live" onClick={() => update({ liveHidden: true }, 'show-live')} className="w-7 h-7 flex items-center justify-center rounded-md bg-black/65 text-white hover:bg-black/80">
          <svg aria-hidden="true" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </span>
    </>
  ) : layout === 'large' && !recording && !noClip ? (
    <button type="button" data-focus="back-to-clip" onClick={() => update({ liveLarge: false }, 'make-large')} className="absolute right-3 bottom-14 h-9 px-3 flex items-center gap-2 rounded-md bg-black/70 border border-white/30 text-sm font-semibold text-white hover:bg-black/85">
      Back to the clip
    </button>
  ) : undefined

  // The same three places whether or not an incident is open — its header,
  // the stage, and what follows — so the stream inside the stage stays put.
  return (
    <div ref={root} className={cluster ? 'h-full relative overflow-y-auto scrollbar-thin px-4 py-4 sm:px-6 sm:py-5 flex flex-col gap-4' : 'h-full'}>
      {cluster ? (
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2">
          <button type="button" onClick={onBack} className="h-9 pl-2 pr-3 flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 text-sm text-zinc-100 hover:bg-zinc-800">
            <svg aria-hidden="true" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m15 18-6-6 6-6" />
            </svg>
            {backLabel}
          </button>
          <div className="min-w-0 flex flex-col gap-0.5">
            <h1 ref={heading} tabIndex={-1} className={`flex items-center gap-2 text-2xl font-bold tracking-tight ${tamper ? 'text-attention' : 'text-zinc-100'}`}>
              {tamper && (
                <svg aria-hidden="true" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
                </svg>
              )}
              {incidentTitle(cluster)}
              {cluster.score > 0 && <span className="text-base font-mono font-medium tracking-normal text-zinc-400">{Math.round(cluster.score * 100)}%</span>}
            </h1>
            <p className="text-sm text-zinc-400 truncate">{`${cluster.site_name || cluster.site_id} · ${cluster.camera}`}</p>
          </div>
          <div className="flex-1" />
          {/* Speaking to whoever is there is the first thing an incident asks for. */}
          {onSpeak && (
            <button type="button" aria-label="Speak to this camera" onClick={onSpeak} className="h-9 px-3 flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 text-sm font-semibold text-zinc-100 hover:bg-zinc-800">
              <svg aria-hidden="true" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Speak
            </button>
          )}
          <span className={`h-7 px-2.5 inline-flex items-center rounded-full border border-zinc-800 bg-zinc-900 text-[13px] ${large ? 'text-live font-semibold' : 'text-zinc-400'}`}>
            {noClip ? 'Live — no recording' : recording ? 'Live — the clip opens when it ends' : large ? 'Live' : 'Clip'}
          </span>
        </div>
      ) : null}

      <div className={cluster ? 'relative flex flex-col gap-3' : 'h-full'}>
        {cluster && !large ? <ClusterClipPanel key={`clip-${cluster.id}`} cluster={cluster} onBoxSize={setClipBox} onNoRecordings={refuse} /> : null}
        {stream(layout, overlay, cornerTop)}
      </div>

      {noClip ? (
        <p className="px-4 py-3 rounded-lg border border-zinc-800 bg-zinc-900 text-sm text-zinc-400">
          <span className="font-semibold text-zinc-100">This is the camera now, not the moment of the alert.</span>{' '}
          This site keeps no recordings Overwatch can reach. The alert&apos;s picture, in the incident panel, is the record of what was seen.
        </p>
      ) : recording ? (
        <p className="flex items-center gap-3 px-4 py-3 rounded-lg border border-zinc-800 bg-zinc-900 text-sm text-zinc-300">
          <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full bg-fault shrink-0" />
          Still recording. The clip opens here once this incident ends.
        </p>
      ) : cluster && !large && (!withCorner || current.liveHidden) ? (
        <button
          type="button"
          data-focus="show-live"
          onClick={() => update(withCorner ? { liveHidden: false } : { liveLarge: true }, withCorner ? 'make-large' : 'back-to-clip')}
          className="self-start h-10 px-3 flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 text-sm font-semibold text-zinc-100 hover:bg-zinc-800"
        >
          <span aria-hidden="true" className="w-2 h-2 rounded-full bg-fault" />
          Show live
        </button>
      ) : null}
    </div>
  )
}
